import React, { useCallback, useEffect, useRef, useState } from 'react';
import './index.less';

const MIN_THUMB = 24;
// 滚动停止后 thumb 淡出的延时
const HIDE_DELAY = 800;

const emptyThumb = { visible: false, offset: 0, length: 0, cross: 0 };

/**
 * 悬浮滚动条：把目标元素的原生滚动条隐藏（宽度 0）后，
 * 在其上方叠一条绝对定位的 thumb —— 可见，但不占用内容宽度/高度。
 *
 * 原生滚动条一旦可见就必然占位（Chrome 121 起 `overflow: overlay` 已失效），
 * 因此凡是「滚动条不能吃掉内容宽度」的容器都可以用这个组件。
 *
 * 使用方需自行隐藏目标元素的原生滚动条，可直接套用 `.overlay-scrollbar-hidden`：
 *   <div className="wrap" ref={wrapRef}>
 *     <div className="list overlay-scrollbar-hidden">...</div>
 *     <OverlayScrollbar containerRef={wrapRef} selector=".list" />
 *   </div>
 *
 * @param {object}  containerRef 定位容器的 ref（thumb 的 offsetParent，不可是滚动元素本身；
 *                               若其 position 为 static，组件会自动补成 relative）
 * @param {object}  targetRef    滚动元素的 ref；与 selector 二选一
 * @param {string}  selector     在 containerRef 内查找滚动元素的选择器；
 *                               都不传时取容器的第一个子元素
 * @param {string}  direction    'vertical'（默认）| 'horizontal' | 'both'
 * @param {number}  size         thumb 粗细，px（纵向为宽度，横向为高度）
 * @param {number}  gap          thumb 距离滚动区边缘的距离，px（纵向为距右边缘，横向为距底边缘）
 * @param {string}  background   thumb 背景色，任意合法 CSS color；传了之后 hover 不再加深
 * @param {boolean} showOnHover  true：仅在容器 hover / 滚动时淡入；false：常驻显示
 * @param {boolean} showOnScroll true：滚动时临时显示；false：仅由 hover 控制
 * @param {string}  hoverSelector 仅由容器内匹配该选择器的元素触发 hover 显隐
 * @param {number}  horizontalInsetStart 横向轨道左侧内缩，px
 * @param {number}  horizontalInsetEnd   横向轨道右侧内缩，px
 * @param {number}  horizontalThumbLength 固定横向 thumb 长度，px；不传时按内容比例计算
 */
export default function OverlayScrollbar({
  containerRef,
  targetRef,
  selector,
  direction = 'vertical',
  size = 4,
  gap = 0,
  background,
  showOnHover = true,
  showOnScroll = true,
  hoverSelector,
  horizontalInsetStart = 0,
  horizontalInsetEnd = 0,
  horizontalThumbLength,
  className = '',
}) {
  const showVertical = direction === 'vertical' || direction === 'both';
  const showHorizontal = direction === 'horizontal' || direction === 'both';

  const scrollElRef = useRef(null);
  const dragRef = useRef(null);
  const hideTimerRef = useRef(null);
  const hoverTargetRef = useRef(false);
  const thumbHoverRef = useRef(false);

  const [vThumb, setVThumb] = useState(emptyThumb);
  const [hThumb, setHThumb] = useState(emptyThumb);
  const [active, setActive] = useState(!showOnHover);

  // showOnHover 为 false 时始终展示
  useEffect(() => {
    if (!showOnHover) setActive(true);
  }, [showOnHover]);

  const sync = useCallback(() => {
    const el = scrollElRef.current;
    const container = containerRef?.current;
    if (!el || !container) return;

    const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = el;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    // thumb 相对定位容器，需要带上滚动区自身的偏移（如表头占据的高度）
    const offsetTop = elRect.top - containerRect.top;
    const offsetLeft = elRect.left - containerRect.left;

    if (showVertical) {
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 1) {
        setVThumb((prev) => (prev.visible ? emptyThumb : prev));
      } else {
        const length = Math.max((clientHeight / scrollHeight) * clientHeight, MIN_THUMB);
        setVThumb({
          visible: true,
          offset: offsetTop + (scrollTop / maxScroll) * (clientHeight - length),
          length,
          cross: offsetLeft + clientWidth - size - gap,
        });
      }
    }

    if (showHorizontal) {
      const maxScroll = scrollWidth - clientWidth;
      const trackLength = Math.max(clientWidth - horizontalInsetStart - horizontalInsetEnd, 0);
      if (maxScroll <= 1 || trackLength <= 0) {
        setHThumb((prev) => (prev.visible ? emptyThumb : prev));
      } else {
        const proportionalLength = (clientWidth / scrollWidth) * trackLength;
        const length = Math.min(
          trackLength,
          Math.max(horizontalThumbLength ?? proportionalLength, MIN_THUMB)
        );
        setHThumb({
          visible: true,
          offset:
            offsetLeft +
            horizontalInsetStart +
            (scrollLeft / maxScroll) * (trackLength - length),
          length,
          cross: offsetTop + clientHeight - size - gap,
        });
      }
    }
  }, [
    containerRef,
    showVertical,
    showHorizontal,
    size,
    gap,
    horizontalInsetStart,
    horizontalInsetEnd,
    horizontalThumbLength,
  ]);

  // 滚动时唤出 thumb，停止后淡出
  const wakeUp = useCallback(() => {
    if (!showOnHover) return;
    const isInteracting =
      hoverTargetRef.current || thumbHoverRef.current || dragRef.current;
    if (!showOnScroll && !isInteracting) {
      setActive(false);
      return;
    }
    setActive(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (
        !dragRef.current &&
        !hoverTargetRef.current &&
        !thumbHoverRef.current
      ) {
        setActive(false);
      }
    }, HIDE_DELAY);
  }, [showOnHover, showOnScroll]);

  const onScroll = useCallback(() => {
    sync();
    wakeUp();
  }, [sync, wakeUp]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return undefined;

    // thumb 是 absolute 的，定位容器必须脱离 static
    if (window.getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    let detach = null;
    const attach = () => {
      const el =
        targetRef?.current ||
        (selector ? container.querySelector(selector) : container.firstElementChild);
      if (!el || el === scrollElRef.current) return;

      detach?.();
      scrollElRef.current = el;
      el.addEventListener('scroll', onScroll, { passive: true });

      const observer = new ResizeObserver(sync);
      observer.observe(el);
      // 容器尺寸不变、内容变高时（如表格增删行）也要重算
      if (el.firstElementChild) observer.observe(el.firstElementChild);

      detach = () => {
        el.removeEventListener('scroll', onScroll);
        observer.disconnect();
      };
      sync();
    };

    attach();
    // 滚动元素可能是条件渲染 / 重挂载后换了节点，丢了就重新找
    let hoverEl = null;
    const onEnter = () => {
      hoverTargetRef.current = true;
      if (showOnHover) setActive(true);
    };
    const onLeave = () => {
      hoverTargetRef.current = false;
      if (showOnHover && !thumbHoverRef.current && !dragRef.current) {
        setActive(false);
      }
    };
    const detachHover = () => {
      hoverEl?.removeEventListener('mouseenter', onEnter);
      hoverEl?.removeEventListener('mouseleave', onLeave);
      hoverEl = null;
      hoverTargetRef.current = false;
    };
    const attachHover = () => {
      const nextHoverEl = hoverSelector
        ? container.querySelector(hoverSelector)
        : container;
      if (!nextHoverEl || nextHoverEl === hoverEl) return;
      detachHover();
      hoverEl = nextHoverEl;
      hoverEl.addEventListener('mouseenter', onEnter);
      hoverEl.addEventListener('mouseleave', onLeave);
    };

    attachHover();
    const mutation = new MutationObserver(() => {
      if (!scrollElRef.current || !container.contains(scrollElRef.current)) attach();
      if (hoverEl && !container.contains(hoverEl)) {
        detachHover();
        if (showOnHover && !thumbHoverRef.current && !dragRef.current) {
          setActive(false);
        }
      }
      if (!hoverEl) attachHover();
    });
    mutation.observe(container, { childList: true, subtree: true });

    return () => {
      mutation.disconnect();
      detach?.();
      detachHover();
      scrollElRef.current = null;
      thumbHoverRef.current = false;
      clearTimeout(hideTimerRef.current);
    };
  }, [containerRef, targetRef, selector, hoverSelector, onScroll, sync, showOnHover]);

  const onDragMove = useCallback((e) => {
    const el = scrollElRef.current;
    const start = dragRef.current;
    if (!el || !start) return;

    const vertical = start.axis === 'vertical';
    const clientSize = vertical ? el.clientHeight : el.clientWidth;
    const scrollSize = vertical ? el.scrollHeight : el.scrollWidth;
    const trackLength = vertical
      ? clientSize
      : Math.max(clientSize - horizontalInsetStart - horizontalInsetEnd, 0);
    const proportionalLength = (clientSize / scrollSize) * trackLength;
    const thumbLength = Math.min(
      trackLength,
      Math.max(
        vertical ? proportionalLength : horizontalThumbLength ?? proportionalLength,
        MIN_THUMB
      )
    );
    const trackRange = trackLength - thumbLength;
    if (trackRange <= 0) return;

    const delta = (vertical ? e.clientY : e.clientX) - start.client;
    const moved = start.scroll + delta * ((scrollSize - clientSize) / trackRange);
    if (vertical) el.scrollTop = moved;
    else el.scrollLeft = moved;
  }, [horizontalInsetStart, horizontalInsetEnd, horizontalThumbLength]);

  const onDragEnd = useCallback(() => {
    const wasDragging = Boolean(dragRef.current);
    dragRef.current = null;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    if (wasDragging) wakeUp();
  }, [onDragMove, wakeUp]);

  const startDrag = (axis) => (e) => {
    const el = scrollElRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      axis,
      client: axis === 'vertical' ? e.clientY : e.clientX,
      scroll: axis === 'vertical' ? el.scrollTop : el.scrollLeft,
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
  };

  useEffect(() => () => {
    dragRef.current = null;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
  }, [onDragMove, onDragEnd]);

  const thumbClass = `overlay-scrollbar-thumb ${active ? 'is-active' : ''} ${className}`;
  // 走 CSS 变量而不是直接写 background，否则内联样式会盖掉 :hover 的加深效果
  const colorStyle = background ? { '--overlay-scrollbar-bg': background } : undefined;
  const onThumbEnter = () => {
    thumbHoverRef.current = true;
    if (showOnHover) setActive(true);
  };
  const onThumbLeave = () => {
    thumbHoverRef.current = false;
    if (showOnHover && !hoverTargetRef.current && !dragRef.current) {
      setActive(false);
    }
  };

  return (
    <>
      {showVertical && vThumb.visible && (
        <div
          className={thumbClass}
          style={{
            ...colorStyle,
            top: vThumb.offset,
            left: vThumb.cross,
            height: vThumb.length,
            width: size,
          }}
          onMouseDown={startDrag('vertical')}
          onMouseEnter={onThumbEnter}
          onMouseLeave={onThumbLeave}
        />
      )}
      {showHorizontal && hThumb.visible && (
        <div
          className={thumbClass}
          style={{
            ...colorStyle,
            left: hThumb.offset,
            top: hThumb.cross,
            width: hThumb.length,
            height: size,
          }}
          onMouseDown={startDrag('horizontal')}
          onMouseEnter={onThumbEnter}
          onMouseLeave={onThumbLeave}
        />
      )}
    </>
  );
}
